import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { FileDropzone } from "@/components/FileDropzone";
import { ExcelPreview } from "@/components/ExcelPreview";
import { WordPreview } from "@/components/WordPreview";
import { LoadingAd } from "@/components/ads/LoadingAd";
import { PreviewBoundary } from "@/components/PreviewBoundary";
import { ProgressBar, stageLabel } from "@/components/ProgressBar";
import { InfoSections } from "@/components/InfoSections";
import { GUIDES } from "@/content/seoContent";
import { takePendingFile } from "@/lib/fileBridge";
import {
  startConversion,
  pollJob,
  regenerateExcel,
  regenerateWord,
  downloadBase64,
  apiErrorMessage,
  XLSX_MIME,
  DOCX_MIME,
} from "@/lib/dotecApi";
import {
  Loader2,
  Download,
  Sparkles,
  Wand2,
  AlertTriangle,
  RotateCcw,
  FileSpreadsheet,
  FileText,
  ArrowLeft,
} from "lucide-react";

export default function Converter({ target }) {
  const isExcel = target === "excel";
  const { t, lang } = useLang();
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("auto");
  const [instructions, setInstructions] = useState("");
  const [status, setStatus] = useState("idle"); // idle | processing | done | error
  const [result, setResult] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [prog, setProg] = useState({ pct: 0, stage: "upload" });

  useEffect(() => {
    const pending = takePendingFile();
    if (pending) setFile(pending);
  }, []);

  const reset = () => {
    setStatus("idle");
    setResult(null);
    setSheets([]);
    setBlocks([]);
  };

  const handleConvert = async () => {
    if (!file) {
      toast.error(t.selectFileFirst);
      return;
    }
    if (mode === "custom" && !instructions.trim()) {
      toast.error(t.instrRequired);
      return;
    }
    setStatus("processing");
    setResult(null);
    setProg({ pct: 5, stage: "upload" });
    try {
      const { job_id } = await startConversion({ file, mode, instructions, target });
      setProg({ pct: 10, stage: "upload" });
      const job = await pollJob(job_id, {
        onProgress: (j) => setProg({ pct: j.progress ?? 0, stage: j.stage || "extract" }),
      });
      if (job.status === "error") {
        setStatus("error");
        setResult(job);
        toast.error(job.error || t.genericError);
        return;
      }
      setResult(job);
      if (isExcel) setSheets(job.sheets_preview || []);
      else setBlocks(job.blocks_preview || []);
      setStatus("done");
      toast.success(t.convDone);
    } catch (e) {
      const msg = apiErrorMessage(e, t);
      setStatus("error");
      setResult({ error: msg });
      toast.error(msg);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    setDownloading(true);
    try {
      if (isExcel) {
        const data = await regenerateExcel(result.job_id, sheets);
        downloadBase64(data.excel_base64, data.filename, XLSX_MIME);
      } else {
        const data = await regenerateWord(result.job_id, blocks);
        downloadBase64(data.file_base64, data.filename, DOCX_MIME);
      }
      toast.success(t.downloadStarted);
    } catch (e) {
      toast.error(t.downloadFailed);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8">
      <div className="mb-6 flex justify-start">
        <Link
          to="/"
          data-testid="back-home-link"
          className="group inline-flex items-center gap-2 rounded-full border-2 border-emerald-500 bg-emerald-50 px-5 py-2.5 text-sm font-bold text-emerald-700 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-emerald-500 hover:text-white hover:shadow-md"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          {t.backHome}
        </Link>
      </div>
      <div className="mb-8 text-center">
        <div
          className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${
            isExcel ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"
          }`}
        >
          {isExcel ? (
            <FileSpreadsheet className="h-7 w-7" />
          ) : (
            <FileText className="h-7 w-7" />
          )}
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {isExcel ? t.excelTitle : t.wordTitle}
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-500">
          {isExcel ? t.excelSub : t.wordSub}
        </p>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <FileDropzone
          file={file}
          onFile={(f) => {
            setFile(f);
            reset();
          }}
          title={t.dropTitle}
          hint={t.dropExcelHint}
          onError={(m) => toast.error(m)}
        />

        {/* Mode toggle */}
        <div className="mt-6">
          <div className="mb-3 flex rounded-xl bg-slate-100 p-1">
            {[
              { key: "auto", label: t.automatic, icon: Sparkles },
              { key: "custom", label: t.custom, icon: Wand2 },
            ].map((m) => (
              <button
                key={m.key}
                data-testid={`mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all ${
                  mode === m.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <m.icon className="h-4 w-4" />
                {m.label}
              </button>
            ))}
          </div>
          {mode === "custom" && (
            <textarea
              data-testid="instructions-input"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={t.instructionsPlaceholder}
              rows={4}
              className="w-full resize-none rounded-xl border border-slate-200 p-4 text-sm outline-none transition-colors focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
          )}
          <p className="mt-2 text-xs text-slate-400">
            {mode === "auto" ? t.autoHint : t.customHint}
          </p>
        </div>

        <button
          data-testid="convert-button"
          onClick={handleConvert}
          disabled={status === "processing" || !file}
          className={`mt-6 flex w-full items-center justify-center gap-2 rounded-xl py-4 text-base font-bold text-white shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
            isExcel
              ? "bg-emerald-600 hover:bg-emerald-700"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {status === "processing" ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t.converting}
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              {isExcel ? t.convertToExcel : t.convertToWord}
            </>
          )}
        </button>

        {status === "processing" && (
          <div className="mt-6">
            <ProgressBar progress={prog.pct} label={stageLabel(t, prog.stage)} />
            <p className="mt-3 text-center text-sm text-slate-500">
              {t.processingNote}
            </p>
          </div>
        )}
      </div>

      {/* Ad shown while the conversion is being processed */}
      {status === "processing" && <LoadingAd />}

      {/* Result */}
      {status === "done" && result && (
        <div
          data-testid="result-panel"
          className="dotec-fade-up mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">
                {t.resultPreview}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {result.pages_processed} {t.pagesLabel} • {result.tables_found} {t.tablesLabel}
                {result.ocr_used ? " • OCR AI vision" : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={reset}
                data-testid="new-conversion-button"
                className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
              >
                <RotateCcw className="h-4 w-4" />
                {t.newBtn}
              </button>
              <button
                onClick={handleDownload}
                data-testid="download-button"
                disabled={downloading}
                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors disabled:opacity-60 ${
                  isExcel
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {t.downloadBtn} {isExcel ? ".xlsx" : ".docx"}
              </button>
            </div>
          </div>

          {result.warnings && result.warnings.length > 0 && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <ul className="space-y-1 text-sm text-amber-800">
                  {result.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <PreviewBoundary message={t.previewFailed}>
            {isExcel ? (
              <ExcelPreview sheets={sheets} onChange={setSheets} />
            ) : (
              <WordPreview blocks={blocks} onChange={setBlocks} />
            )}
          </PreviewBoundary>
        </div>
      )}

      {status === "error" && (
        <div data-testid="error-panel" className="dotec-fade-up mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-red-500" />
          <p data-testid="error-message" className="font-semibold text-red-800">
            {result?.error || t.genericError}
          </p>
          <button
            onClick={reset}
            className="mt-4 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700"
          >
            {t.newBtn}
          </button>
        </div>
      )}
      {/* Tool guide (SEO / AdSense long-form content) */}
      <div className="mt-4 rounded-3xl border border-slate-100 bg-white/60">
        <InfoSections
          sections={GUIDES[isExcel ? "excel" : "word"][lang] || GUIDES[isExcel ? "excel" : "word"].en}
          testId="converter-guide"
        />
      </div>
    </div>
  );
}
